import { useEffect, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Check, Phone, PackageCheck, Truck, Wallet } from "lucide-react";

import { formatDH } from "@/lib/shop-store";
import { readConfirmedOrder, type ConfirmedOrder } from "@/lib/order-confirmation";

export const Route = createFileRoute("/commande-confirmee")({
  head: () => ({
    meta: [
      { title: "Commande confirmée — Sunset Concept Store" },
      {
        name: "description",
        content:
          "Votre commande Sunset Concept Store est enregistrée. Notre équipe vous contacte par téléphone avant l'expédition.",
      },
      { name: "robots", content: "noindex" },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Commande confirmée — Sunset Concept Store" },
      {
        property: "og:description",
        content: "Merci pour votre commande. Paiement à la livraison partout au Maroc.",
      },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: OrderConfirmedPage,
});

const STEPS = [
  { icon: Check, label: "Votre commande est enregistrée." },
  { icon: Phone, label: "Notre équipe vous contacte pour confirmation." },
  { icon: PackageCheck, label: "Votre colis est préparé et expédié." },
  { icon: Wallet, label: "Vous payez à la réception." },
];

function OrderConfirmedPage() {
  const [order, setOrder] = useState<ConfirmedOrder | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setOrder(readConfirmedOrder());
    setReady(true);
  }, []);

  return (
    <main className="mx-auto min-h-[70vh] max-w-2xl px-6 py-20 md:py-28">
      <div className="flex flex-col items-center text-center">
        <div className="grid h-16 w-16 place-items-center rounded-full border border-foreground/15 bg-secondary">
          <Check size={26} strokeWidth={1.2} className="text-foreground" />
        </div>
        <p className="label-xs mt-8 text-muted-foreground">Commande confirmée ✓</p>
        <h1 className="mt-4 font-serif text-3xl font-light tracking-tight md:text-4xl">
          Merci pour votre commande.
        </h1>
        <p className="mt-5 max-w-md text-sm leading-relaxed text-muted-foreground">
          Votre commande a bien été enregistrée. Notre équipe vous contactera prochainement par
          téléphone pour confirmer les informations avant l'expédition.
        </p>
      </div>

      {ready && order ? (
        <section className="mt-14 rounded-sm border border-border bg-card/40 p-6 md:p-8">
          <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border pb-5">
            <span className="label-xs text-muted-foreground">Numéro de commande</span>
            <span className="font-serif text-xl">{order.orderNumber}</span>
          </div>

          <ul className="divide-y divide-border">
            {order.lines.map((line, i) => (
              <li key={`${line.name}-${i}`} className="flex items-start justify-between gap-4 py-5">
                <div className="min-w-0">
                  <p className="font-serif text-lg leading-snug">{line.name}</p>
                  {line.variant ? (
                    <p className="label-xs mt-1 text-muted-foreground">{line.variant}</p>
                  ) : null}
                  <p className="label-xs mt-1 text-muted-foreground">Quantité : {line.quantity}</p>
                </div>
                <p className="shrink-0 text-sm">{formatDH(line.price * line.quantity)}</p>
              </li>
            ))}
          </ul>

          <div className="flex items-baseline justify-between border-t border-border pt-5">
            <span className="label-xs text-muted-foreground">Total</span>
            <span className="font-serif text-2xl">{formatDH(order.total)}</span>
          </div>

          <dl className="mt-6 space-y-3 border-t border-border pt-6 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="label-xs text-muted-foreground">Client</dt>
              <dd className="text-right">{order.fullName}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="label-xs text-muted-foreground">Ville</dt>
              <dd className="text-right">{order.city}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="label-xs text-muted-foreground">Paiement</dt>
              <dd className="flex items-center gap-2 text-right">
                <Truck size={15} strokeWidth={1.3} className="text-muted-foreground" />
                Paiement à la livraison
              </dd>
            </div>
          </dl>
        </section>
      ) : ready ? (
        <p className="mt-14 text-center text-sm text-muted-foreground">
          Le détail de cette commande n'est plus disponible sur cet appareil. Vous pouvez suivre
          votre commande depuis le lien reçu ou nous contacter.
        </p>
      ) : null}

      <section className="mt-14">
        <h2 className="font-serif text-2xl font-light tracking-tight">Et maintenant ?</h2>
        <ol className="mt-6 space-y-5">
          {STEPS.map(({ icon: Icon, label }, i) => (
            <li key={label} className="flex items-start gap-4">
              <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full border border-border bg-secondary">
                <Icon size={15} strokeWidth={1.3} className="text-foreground" />
              </span>
              <span className="text-sm leading-relaxed text-muted-foreground">
                <span className="label-xs mr-2 text-foreground">{`0${i + 1}`}</span>
                {label}
              </span>
            </li>
          ))}
        </ol>
      </section>

      <div className="mt-14 flex justify-center">
        <Link
          to="/"
          className="label-xs border border-foreground px-10 py-4 transition-colors hover:bg-primary hover:text-primary-foreground"
        >
          Continuer mes achats
        </Link>
      </div>
    </main>
  );
}
